import type { FC, Ref } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Tooltip } from '@mui/material';
import type { TreeItemContentProps } from '@mui/x-tree-view';
import { useTreeItemState } from '@mui/x-tree-view';

import { noDataAvailableStyles } from 'src/theme/sharedStyles';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import { useNpsQuestionBarSegmentColors } from 'src/components/dashboard/DashboardPage/widgets/DeepDiveQuestionsWidget/hooks';

import SentimentTreeLabel from '../TreeItem/SentimentTreeLabel';

import HRQSOpenCommentBarSegment from './HRQSOpenCommentBarSegment';

const HRQSOpenCommentBar: FC<TreeItemContentProps> = React.forwardRef(
  function CustomContent(
    {
      treeData,
      highestCount,
      isComparison,
      handleSelectionClick,
      sentimentFilter,
      itemId,
      classes,
      label,
      icon: iconProp,
      expansionIcon,
    },
    ref: React.ForwardedRef<HTMLLIElement>,
  ) {
    const { t } = useTranslation('dashboard');

    const { regular, comparison } = useNpsQuestionBarSegmentColors();
    const { handleExpansion } = useTreeItemState(itemId);
    const showNeutralBar =
      !!treeData.neutralCount &&
      (sentimentFilter === 'totalOfAllCounts' ||
        sentimentFilter === 'neutralCount');

    const showPositiveBar =
      !!treeData.positiveCount &&
      (sentimentFilter === 'totalOfAllCounts' ||
        sentimentFilter === 'positiveCount');

    const showNegativeBar =
      !!treeData.negativeCount &&
      (sentimentFilter === 'totalOfAllCounts' ||
        sentimentFilter === 'negativeCount');

    const tooltip = [
      `${t('OpenComments.negativeTopics')}: ${treeData.negativeCount}`,
      `${t('OpenComments.neutralTopics')}: ${treeData.neutralCount}`,
      `${t('OpenComments.positiveTopics')}: ${treeData.positiveCount}`,
    ].join(' | ');
    const comparisonTooltip = treeData.comparison
      ? [
          `${t('OpenComments.negativeTopics')}: ${treeData.comparison.negativeCount}`,
          `${t('OpenComments.neutralTopics')}: ${treeData.comparison.neutralCount}`,
          `${t('OpenComments.positiveTopics')}: ${treeData.comparison.positiveCount}`,
        ].join(' | ')
      : '';
    return (
      <Stack direction="row" ref={ref as Ref<HTMLDivElement>}>
        <SentimentTreeLabel
          treeData={treeData}
          sentimentFilter={sentimentFilter}
          handleSelectionClick={handleSelectionClick}
          handleExpansionClick={handleExpansion}
          label={label}
          icon={iconProp || expansionIcon}
          labelClasses={classes.label}
        />
        <Stack sx={{ flexGrow: 1 }}>
          <Tooltip
            arrow
            enterTouchDelay={1}
            followCursor
            leaveTouchDelay={3000}
            placement="top"
            title={tooltip}
          >
            <Stack
              direction="row"
              sx={{
                height: '3.625rem',
                mt: isComparison ? 0.5 : 1,
                flexGrow: 1,
              }}
            >
              {highestCount <= 0 ? (
                <Box sx={noDataAvailableStyles.noDataAvailable} />
              ) : (
                <>
                  {showNegativeBar && (
                    <HRQSOpenCommentBarSegment
                      percentage={getRelativeValue(
                        treeData.negativeCount,
                        highestCount,
                        0,
                      )}
                      absoluteValue={treeData.negativeCount}
                      {...regular.detractor}
                      handleClick={() =>
                        handleSelectionClick(treeData.negativeId)
                      }
                    />
                  )}
                  {showNeutralBar && (
                    <HRQSOpenCommentBarSegment
                      percentage={getRelativeValue(
                        treeData.neutralCount,
                        highestCount,
                        0,
                      )}
                      absoluteValue={treeData.neutralCount}
                      {...regular.passive}
                      handleClick={() =>
                        handleSelectionClick(treeData.neutralId)
                      }
                    />
                  )}
                  {showPositiveBar && (
                    <HRQSOpenCommentBarSegment
                      percentage={getRelativeValue(
                        treeData.positiveCount,
                        highestCount,
                        0,
                      )}
                      absoluteValue={treeData.positiveCount}
                      {...regular.promoter}
                      handleClick={() =>
                        handleSelectionClick(treeData.positiveId)
                      }
                    />
                  )}
                </>
              )}
            </Stack>
          </Tooltip>

          {treeData.comparison && (
            <Tooltip
              arrow
              enterTouchDelay={1}
              followCursor
              leaveTouchDelay={3000}
              placement="top"
              title={comparisonTooltip}
            >
              <Stack
                direction="row"
                sx={{
                  height: '1.5rem',
                  mt: isComparison ? 0.5 : 1,
                  flexGrow: 1,
                }}
              >
                {highestCount <= 0 ? (
                  <Box sx={noDataAvailableStyles.noDataAvailable} />
                ) : (
                  <>
                    {showNegativeBar && (
                      <HRQSOpenCommentBarSegment
                        percentage={getRelativeValue(
                          treeData.negativeCount,
                          highestCount,
                          0,
                        )}
                        absoluteValue={treeData.negativeCount}
                        {...comparison.detractor}
                        handleClick={() =>
                          handleSelectionClick(treeData.negativeId)
                        }
                      />
                    )}
                    {showNeutralBar && (
                      <HRQSOpenCommentBarSegment
                        percentage={getRelativeValue(
                          treeData.neutralCount,
                          highestCount,
                          0,
                        )}
                        absoluteValue={treeData.neutralCount}
                        {...comparison.passive}
                        handleClick={() =>
                          handleSelectionClick(treeData.neutralId)
                        }
                      />
                    )}
                    {showPositiveBar && (
                      <HRQSOpenCommentBarSegment
                        percentage={getRelativeValue(
                          treeData.positiveCount,
                          highestCount,
                          0,
                        )}
                        absoluteValue={treeData.positiveCount}
                        {...comparison.promoter}
                        handleClick={() =>
                          handleSelectionClick(treeData.positiveId)
                        }
                      />
                    )}
                  </>
                )}
              </Stack>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  },
);

export default HRQSOpenCommentBar;
